import React, {useEffect, useRef, useState} from "react";
import {Link, useLocation, useNavigate, useParams} from "react-router-dom";
import '../scss/SupportRequest.scss';
import {
    GetAPIRoot,
    GetCodes,
    GetColumnConfig,
    GetCommentOptions, GetContact, GetContactRoles,
    GetContacts, GetMessageSeenText, GetMessageTemplate, GetMessageTemplatePassive, GetMessageWasSentToYou, GetProviderHasSeenYourMessageText,
    GetRequestComments,
    GetRequestMessages, GetRequestSeenSms, GetSentYouAMessage, GetSMSSendEnabled,
    GetSupportNeed, GetSurveyWithId, GetUser, GetUserBySurvey, GetUsers,
    PostSendEmail,
    PostSendSMS,
    PostSupportNeedComment,
    PostSupportNeedUpdate
} from "../API";
import {ReactComponent as BackArrow} from "../svg/back-arrow.svg";
import {ReactComponent as SeenIcon} from "../svg/success.svg";
import {ReactComponent as EyeIcon} from "../svg/seen.svg";
import {ReactComponent as ErrorIcon} from "../svg/warning.svg";
import {ReactComponent as CommentIcon} from "../svg/comment.svg";
import {ReactComponent as PenIcon} from "../svg/pen.svg";
import {ReactComponent as HighFive} from "../svg/high-five.svg"
import {ReactComponent as MailIcon} from "../svg/mail.svg"
import {ReactComponent as ErrorBoy} from "../svg/error-boy.svg"
import {ReactComponent as MoreIcon} from "../svg/more.svg"
import {ReactComponent as Chevron} from "../svg/chevron.svg"
import {ReactComponent as InfoIcon} from "../svg/info.svg"
import {ReactComponent as UserIcon} from "../svg/user.svg"
import {ReactComponent as LoadingIcon} from "../svg/loading.svg";
import {ReactComponent as MessageIcon} from "../svg/message.svg";
import {ReactComponent as NextArrow} from "../svg/next-arrow.svg";
import {findUserById, formatDate, getStudentFromContacts, getTopicDisplayName, renderUser, useMediaQuery} from "../formats";
import {HoverTooltip, Popover, Popup, RequestStatus, Skeleton, Switch, Tooltip} from "../UIElements";
import {RequestInfo} from "./SupportNeedsView";
import {FormattedMessage, useIntl} from "react-intl";
import {useConfigData, useCurrentUserData, useLanguageData} from "../App";
import {useSnackbar} from "../Content";

const WHATSAPP_MESSAGE_MAX = 500
const sanitizeTemplateVar = (s) =>
    (s || "").replace(/[\r\n\t]+/g, " ").replace(/ {5,}/g, "    ").trim()

export default function SupportRequest() {
    const { requestId } = useParams()
    const getUsers = GetUsers()
    const { status, data, reFetchSupportNeed, reFetchRequestComments } = GetRequestWithId(requestId)
    const loading = status === "loading" || getUsers.isLoading
    const error = status === "error" || getUsers.isError
    const success = status === "success" && getUsers.isSuccess
    const noSuccess = status === "404"
    const noUserRights = status === "401"
    const intl = useIntl()
    const isMobile = useMediaQuery('(max-width: 678px)')
    const {currentUserData} = useCurrentUserData()
    const isTeacher = currentUserData?.groupView
    const location = useLocation()
    const fromLocation = location?.state?.from?.pathname || "/requests"

    return <div className={"support-request-container"}>
        <nav>
            {isTeacher ?
                <Link to={fromLocation} state={{animation:"true",direction:'right'}}>
                    <BackArrow />{intl.formatMessage({id:"back", defaultMessage:"Back"})}
                </Link> :
                <Link to={"/"} state={{animation:"true",direction:'right'}}>
                    <BackArrow />
                    {isMobile ?
                        intl.formatMessage({
                            id: 'all',
                            defaultMessage: 'All',
                        }) :
                        intl.formatMessage({
                            id: 'allSupportRequests',
                            defaultMessage: 'All support requests',
                        })
                    }
                </Link>
            }
        </nav>
        <div className={"support-request-wrapper"}>
            {loading &&
                <SupportRequestSkeleton />
            }
            {success &&
                <SupportRequestContent
                    request={data.request}
                    codes={data.codes}
                    columnConfig={data.columns}
                    commentOptions={data.comments}
                    requestComments={data.requestComments}
                    requestHistory={data.requestHistory}
                    smsEnabled={data.smsEnabled}
                    requestSeenSmsEnabled={data.requestSeenSmsEnabled}
                    reFetchRequestComments={() => reFetchRequestComments()}
                    reFetchRequest={() => reFetchSupportNeed()}
                    messagesData={data.messages}
                    userData={getUsers?.data}
                    survey={data?.survey}
                />
            }
            {error &&
                <FailedToLoad />
            }
            {noSuccess &&
                <NoRequestWithId />
            }
            {noUserRights &&
                <NoRightsForRequest />
            }
        </div>
    </div>
}

function SupportRequestContent({request, codes, columnConfig, commentOptions, requestComments, requestHistory, reFetchRequestComments, reFetchRequest, smsEnabled, requestSeenSmsEnabled, messagesData, userData, survey}) {
    const intl = useIntl()
    const category = request.categoryName ? request.categoryName : intl.formatMessage({
        id: 'missingNameForCategory',
        defaultMessage: 'Name missing for support category',
    })
    useEffect(() => {
        document.title = request.studentName + " | Annie Advisor"
    }, [request])

    const followupStatus = request.hasOwnProperty("followupstatus") && request.followupstatus ? request.followupstatus : ""
    const followUpActive = request.hasOwnProperty("followupstatus") && request.followupstatus !== null && request.followupstatus !== "FINISHED"

    return <>
        <div className={"support-request"}>
            <div className={"support-request-header"}>
                <div>
                    <RequestStatus
                        status={request.status}
                        followup={followupStatus}
                        survey={request?.survey}
                    />
                    <h1>{request.studentName}</h1>
                    <h2>{category}</h2>
                </div>
                <RequestInfo request={request} />
            </div>
            <StudentInfo
                student={request?.contactmeta}
                columnConfig={columnConfig}
            />
            <RequestHistory
                request={request}
                codes={codes}
                requestComments={requestComments}
                requestHistory={requestHistory}
                userData={userData}
            />
            {followUpActive ?
                <FollowUpWarning /> :
                <RequestActions
                    request={request}
                    comments={commentOptions}
                    reFetchRequestComments={reFetchRequestComments}
                    reFetchRequest={reFetchRequest}
                    requestHistory={requestHistory}
                    smsEnabled={smsEnabled}
                    requestSeenSmsEnabled={requestSeenSmsEnabled}
                    messagesData={messagesData}
                    survey={survey}
                />
            }
        </div>
    </>
}

export function AssignedProviders({request}) {
    const {configData} = useConfigData()
    const rolesConfig = configData?.roles ? Object.keys(configData.roles) : []
    const assignedToRole = rolesConfig.includes(request?.supporttype)
    const assignedToInformation = request?.supporttype === "INFORMATION"
    const assignedToProvider = request?.supporttype === "SUPPORTPROVIDER"

    const intl = useIntl()
    if (assignedToRole) {
        return <ContactRoleInfo request={request} />
    }
    if (assignedToProvider) {
        return <SupportNeedProvider request={request}/>
    }
    if (assignedToInformation) {
        return <p><em><FormattedMessage
            id={"notAssigned"}
            defaultMessage={"Not assigned to a provider."}
            values={{br: <br/>}}/>
           </em></p>
    }

    return <p><em>{intl.formatMessage({id:"noSupportType", defaultMessage:"The support request is not assigned."})}</em></p>
}

function SupportNeedProvider({request}) {
    const intl = useIntl()
    const getSupportProviders = GetUserBySurvey(request.survey)
    if (getSupportProviders.isSuccess) {
        return <SupportProviderDisplay request={request} supportProviders={getSupportProviders?.data}/>
    }
    if (getSupportProviders.isError) {
        return <p>{intl.formatMessage({id:"loadingError",defaultMessage:"Error in loading information"})}</p>
    }

    return <p>{intl.formatMessage({id:"loading",defaultMessage:"Loading..."})}</p>
}

function SupportProviderDisplay({request, supportProviders}) {
    const intl = useIntl()
    const supportCategory = request?.category
    const usersAssignedToCategory = supportProviders.filter(user => user?.meta && user.meta.hasOwnProperty("category") && user.meta.category[supportCategory]) // Check that category is set true

    if (usersAssignedToCategory?.length < 1) {
        return <p><em>{intl.formatMessage({id:"noProvidersSet", defaultMessage:"No support providers set for this request."})}</em></p>
    }
    return <ul className={"dotted"}>
        {usersAssignedToCategory.map((user,i) => {
            return <li key={i}>{GetUserName(user?.annieuser)}</li>
        })}
    </ul>
}

function ContactRoleInfo({request}) {
    const intl = useIntl()
    const contactId = request?.contact
    const getContactRoles = GetContactRoles(contactId)
    if (getContactRoles.isSuccess) {
        return <ContactRoleDisplay request={request} contactRoles={getContactRoles?.data}/>
    }
    if (getContactRoles.isError) {
        return <p>{intl.formatMessage({id:"loadingError",defaultMessage:"Error in loading information"})}</p>
    }
    return <p>{intl.formatMessage({id:"loading",defaultMessage:"Loading..."})}</p>
}

function ContactRoleDisplay({request, contactRoles}) {
    const intl = useIntl()
    const role = request?.supporttype
    const usersWithRole = contactRoles.filter(user => user?.role === role)

    return <>
        <p>{intl.formatMessage({id:"role."+role, defaultMessage:"Missing role name"})}</p>
        {usersWithRole?.length > 0 ?
            <ul className={"dotted"}>
                {usersWithRole.map((user,i) => {
                    return <li key={i}>{GetUserName(user?.annieuser)}</li>
                })}
            </ul> :
            <p><em>{intl.formatMessage({id:"noUserSetForRole", defaultMessage:"Student doesn't have a provider set for this role"})}</em></p>
        }
    </>
}

export function GetUserName(id) {
    const intl = useIntl()
    const getUser = GetUser(id)
    if (getUser.isLoading) {
        return intl.formatMessage({id:'loading', defaultMessage:'Loading...'})
    }
    if (getUser.isSuccess) {
        const user = getUser.data[0]
        if (!user) {
            return `No user found with id: ${id}`
        }

        if (user?.meta && (user.meta?.firstname || user.meta?.lastname || user?.meta?.userid || user.meta?.email)) {
            return renderUser(user)
        } else {
            return id
        }
    }
    return id
}

function ButtonToRequestsOrGroup({noBackground}) {
    const {currentUserData} = useCurrentUserData()
    const isTeacher = currentUserData?.groupView
    const location = useLocation()
    const intl = useIntl()
    const fromLocation = location?.state?.from?.pathname || "/requests"

    const className = noBackground ? "button no-background to-requests" : "button primary main to-requests"

    return <Link to={isTeacher ? fromLocation : "/"} state={{direction:'right', animation: "true"}} className={className}>
        {!noBackground?
            <>
            {intl.formatMessage({
                id: 'allSupportRequests',
                defaultMessage: 'All support requests',
            })}
            <NextArrow />
            </> :
            intl.formatMessage({
                id: 'done',
                defaultMessage: 'Done',
            })
        }
    </Link>
}

function FollowUpWarning() {
    return <div className={"request-actions"}>
        <div className={"request-actions-container"}>
            <div className={"info-text-container"}>
                <InfoIcon />
                <h4>
                    <FormattedMessage
                        id={'followup.active.description'}
                        defaultMessage={"Support request actions are blocked temporarily while the follow up is active.{br}{br}Actions are activated once the follow up has ended or the student has responded."}
                        values={{br: <br/>}}
                    />
                </h4>
            </div>
            <ButtonToRequestsOrGroup />
        </div>
    </div>
}

function RequestActions({request, comments, reFetchRequestComments, reFetchRequest, requestHistory, smsEnabled, requestSeenSmsEnabled, messagesData, survey}) {
    const requestStatusOpen = request.status === "NEW" || request.status === "OPENED"
    const requestStatusSeen = request.status === "ACKED"
    const requestStatusError = request.status === "ERROR"

    const initialView = requestStatusOpen ? "start" : "comment"
    const [navigateBackTo, setNavigateBackTo] = useState(initialView)
    const [showView, setShowView] = useState(initialView) // start, comment, message, error, finish
    const [activeCompliment, setActiveCompliment] = useState("skip") // error, comment, skip

    const postSupportNeed = PostSupportNeedUpdate()
    const postSupportNeedComment = PostSupportNeedComment()
    const postSendSMS = PostSendSMS()

    const {currentUserData} = useCurrentUserData()

    const userRole = currentUserData?.meta && currentUserData.meta.hasOwnProperty("role") && currentUserData.meta.role ? currentUserData.meta.role+" " : ""
    const userFirstName = currentUserData?.meta && currentUserData.meta.hasOwnProperty("firstname") && currentUserData.meta.firstname ? currentUserData.meta.firstname+" " : ""
    const userLastName = currentUserData?.meta && currentUserData.meta.hasOwnProperty("lastname") && currentUserData.meta.lastname ? currentUserData.meta.lastname+" " : ""
    const userPhoneNumber = (currentUserData?.meta && currentUserData.meta.hasOwnProperty("phonenumber") && currentUserData.meta.phonenumber && currentUserData.meta.phonenumber.length > 0) ? "("+currentUserData.meta.phonenumber+")" : ""
    const userid = currentUserData?.meta?.userid ? `- ${currentUserData.meta.userid}` : (currentUserData?.meta?.email ? `- ${currentUserData.meta.email}` : "")
    //const updatedBy = userFirstName + userLastName + userEmail
    const nameAndUserId = userFirstName + userLastName + userid
    const superuserOrCoordinator = currentUserData.superuser || currentUserData.coordinator

    const supportBotSurvey = survey?.config?.type === "SUPPORTBOT"
    const showSmsButton = smsEnabled && !supportBotSurvey

    const setStatusOpen = () => {
        const supportNeedUpdate = {
            "contact": request.contact,
            "survey": request.survey,
            "category": request.category,
            "status": "OPENED",
            "supporttype": request?.supporttype,
            "followuptype": request?.followuptype,
            "followupresult": request?.followupresult,
            "followupstatus": request?.followupstatus
        }
        postSupportNeed.mutate(supportNeedUpdate, {
            onError: error => {
                console.log(error)
            },
            onSuccess:() => {
                reFetchRequest()
            }
        })
    }

    useEffect(() => {
        if (!currentUserData.superuser && request.status === "NEW") {
            setStatusOpen()
        }
    },[request])

    const sendStudentSms = (smsBody, resolve, reject) => {
        const smsData = {
            "to": request.contactmeta.phonenumber,
            "contact": request.contact,
            "body": smsBody,
            "sender": "Annie",
            "survey": request.survey
        }
        postSendSMS.mutate(smsData, {
            onError: error => {
                reject(error)
                console.log(error)
            },
            onSuccess: () => {
                console.log("SMS sent")
                resolve()
                reFetchRequest()
            }
        })
    }

    const postComment = (comment, resolve, reject) => {
        const commentData = {
            "supportneed": request.id,
            "body": comment,
        }
        postSupportNeedComment.mutate(commentData, {
            onError: error => {
                reject(error)
                console.log(error)
            },
            onSuccess:()=>{
                console.log("Comment: "+comment+" posted!")
                resolve()
            }
        })
    }

    return <div className={"request-actions"}>
        <div className={"request-actions-container"}>
            {showView === "start" &&
                <SetStatusSeen
                    request={request}
                    requestHistory={requestHistory}
                    goToReportError={()=>{
                        setShowView("error")
                        setNavigateBackTo("start")
                    }}
                    goToAddComments={()=>{
                        reFetchRequest()
                        setShowView("comment")
                    }}
                    sendStudentSms={(e,resolve,reject)=>sendStudentSms(e,resolve,reject)}
                    postSupportNeed={postSupportNeed}
                    userRole={userRole}
                    userFirstName={userFirstName}
                    userLastName={userLastName}
                    userPhoneNumber={userPhoneNumber}
                    superuserOrCoordinator={superuserOrCoordinator}
                    requestSeenSmsEnabled={requestSeenSmsEnabled}
                    messagesData={messagesData}
                />
            }
            {showView === "error" &&
            <SendErrorReport
                request={request}
                nameAndUserId={nameAndUserId}
                goBack={()=>{
                    setShowView(navigateBackTo)
                }}
                goToErrorCompliment={()=>{
                    reFetchRequest()
                    reFetchRequestComments()
                    setShowView("finish")
                    setActiveCompliment("error")
                }}
                postComment={(message,resolve, reject)=>postComment(message,resolve,reject)}
                postSupportNeed={postSupportNeed}
            />
            }
            {showView === "message" &&
                <SendMessage
                    request={request}
                    goBack={()=>{
                        setShowView(navigateBackTo)
                    }}
                    goToNextView={()=>{
                        reFetchRequest()
                        setShowView(navigateBackTo)
                        setActiveCompliment("finish")
                    }}
                    sendStudentMessage={(comment,resolve,reject)=>sendStudentSms(comment,resolve,reject)}
                    userRole={userRole}
                    userFirstName={userFirstName}
                    userLastName={userLastName}
                    messagesData={messagesData}
                />
            }
            {showView === "comment" &&
                <AddNotes
                    comments={comments}
                    goToErrorDialog={()=> {
                        setShowView("error")
                        setNavigateBackTo("comment")
                    }}
                    goToCommentCompliment={()=>{
                        setShowView("finish")
                        setActiveCompliment("comment")
                        reFetchRequestComments()
                    }}
                    goToMessageDialog={()=> {
                        setShowView("message")
                        setNavigateBackTo("comment")
                    }}
                    postComment={(comment,resolve,reject)=>postComment(comment,resolve,reject)}
                    smsEnabled={showSmsButton}
                    sendStudentSms={(smsContent)=>sendStudentSms(smsContent)}
                    userRole={userRole}
                    userFirstName={userFirstName}
                    userLastName={userLastName}
                />
            }
            {showView === "finish" &&
                <FinishedView
                    goToView={view=>{
                        setShowView(view)
                        setNavigateBackTo("finish")
                    }}
                    activeCompliment={activeCompliment}
                    smsEnabled={showSmsButton}
                />
            }
        </div>
    </div>
}

function SetStatusSeen({goToReportError, requestHistory, request, postSupportNeed, goToAddComments, sendStudentSms, userRole, userFirstName, userLastName, userPhoneNumber, superuserOrCoordinator, setShowSkipButtonAfterSeen, requestSeenSmsEnabled, messagesData}) {
    const intl = useIntl()
    const [showSeenPopover, setShowSeenPopover] = useState(false)
    const requestHasBeenSeen = requestHistory.some(obj => obj.status === "ACKED")
    const metaForSms = userRole?.length > 0 && userFirstName?.length > 0 && userLastName?.length > 0
    const smsTextForRequestSeen = metaForSms ?
        // {supportProvider} replaced with info of provider. Sanitized so the value stays
        // valid as a WhatsApp template variable (no line breaks / runs of spaces).
        messagesData.hasSeenYourMessageAndWillContact.replace("{supportProvider}", sanitizeTemplateVar(userRole + userFirstName + userLastName + userPhoneNumber)) :
        // Passive form of message
        messagesData.messageSeenAndWillContact

    const {setSnackbarData} = useSnackbar()
    const [loading, setLoading] = useState(false)

    const notifyStudentDefault = requestSeenSmsEnabled ? !requestHasBeenSeen : false

    const [notifyStudent, setNotifyStudent] = useState(notifyStudentDefault)

    const setStatusSeen = (sendSms) => {
        setLoading(true)
        const supportNeedUpdate = {
            "contact": request.contact,
            "survey": request.survey,
            "category": request.category,
            "status": "ACKED",
            "supporttype": request?.supporttype,
            "followuptype": request?.followuptype,
            "followupresult": request?.followupresult,
            "followupstatus": request?.followupstatus
        }
        postSupportNeed.mutate(supportNeedUpdate, {
            onError: error => {
                console.log(error)
                setSnackbarData({
                    show: true,
                    text: intl.formatMessage({id:"markingRequestFailed",defaultMessage:"Marking the request seen failed. Please try again."}),
                    state: "error",
                    autoHide: true,
                })
                setLoading(false)
            },
            onSuccess:() => {
                if (sendSms && smsTextForRequestSeen?.length > 0 && request.hasOwnProperty("contactmeta") && request.contactmeta.phonenumber) {
                    new Promise((resolve, reject) => {
                        sendStudentSms(smsTextForRequestSeen, resolve, reject)
                    }).then(()=>{
                        console.log("SMS sent")
                    }).catch(error => {
                        console.log(error)
                    })
                }
                setSnackbarData({
                    show: true,
                    text: intl.formatMessage({id:"requestMarkedSeen",defaultMessage:"Request marked seen!"}),
                    state: "success",
                    autoHide: true,
                })
                setLoading(false)
                goToAddComments()
            }
        })
    }

    const popoverTriggerRef = useRef(null)

    return <>
    <div className={"button-container"}>
        {
            superuserOrCoordinator ?
                loading ?
                    <div className={"split-button-container"}>
                        <button className={"primary main split-left loading"}>
                            <LoadingIcon />
                            {intl.formatMessage({
                                id: 'markingAsSeen',
                                defaultMessage: 'Marking...',
                            })}
                        </button>
                        <button className={"primary main split-right loading"} ref={popoverTriggerRef}>
                            <Chevron className={showSeenPopover ? "active" : ""}/>
                        </button>
                        {showSeenPopover &&
                            <Popover showPopover={showSeenPopover} setShowPopover={e=>setShowSeenPopover(e)} popoverTriggerRef={popoverTriggerRef}>
                                <button className={"text-button"} onClick={()=>setStatusSeen(false)}>
                                    <EyeIcon />
                                    {intl.formatMessage({
                                        id: 'markAsSeenWithoutSms',
                                        defaultMessage: 'Mark as seen without notifying student',
                                    })}
                                </button>
                            </Popover>
                        }
                    </div> :
                    <>
                        <div className={"split-button-container"}>
                            <button className={"primary main split-left"} onClick={()=>setStatusSeen(notifyStudent)}>
                                <SeenIcon />
                                {intl.formatMessage({
                                    id: 'markAsSeen',
                                    defaultMessage: 'Mark as seen',
                                })}
                            </button>
                            <button className={"primary main split-right"} onClick={()=>setShowSeenPopover(!showSeenPopover)} ref={popoverTriggerRef}>
                                <Chevron className={showSeenPopover ? "active" : ""}/>
                            </button>
                            {showSeenPopover &&
                                <Popover showPopover={showSeenPopover} setShowPopover={e=>setShowSeenPopover(e)} popoverTriggerRef={popoverTriggerRef}>
                                    <button className={"text-button"} onClick={()=>setStatusSeen(false)}>
                                        <EyeIcon />
                                        {intl.formatMessage({
                                            id: 'markAsSeenWithoutSms',
                                            defaultMessage: 'Mark as seen without notifying student',
                                        })}
                                    </button>
                                </Popover>
                            }
                        </div>
                    </>
                :
                loading ?
                    <button className={"primary main loading"}>
                        <LoadingIcon />
                        {intl.formatMessage({
                            id: 'markingAsSeen',
                            defaultMessage: 'Marking...',
                        })}
                    </button> :
                    <button className={"primary main"} onClick={()=>setStatusSeen(notifyStudent)}>
                        <SeenIcon />
                        {intl.formatMessage({
                            id: 'markAsSeen',
                            defaultMessage: 'Mark as seen',
                        })}
                    </button>
        }
        <Tooltip>
            {requestSeenSmsEnabled ?
                <div>
                    <FormattedMessage
                        id={"tooltip.markAsSeen.smsEnabled"}
                        defaultMessage={
                            "Mark the request as seen and proceed with support actions. By marking this request as seen, you let the student know that you're addressing their request. For new requests, students are notified by default."
                        }
                        values={{ br: <br /> }}
                    />
                    <div className={"notify-selection"}>
                        <input type={"checkbox"} checked={notifyStudent} onChange={e => setNotifyStudent(e.target.checked)} id="notifyStudentCheckbox" />
                        <label htmlFor="notifyStudentCheckbox">
                            {intl.formatMessage({
                                    defaultMessage: 'Notify the Student',
                                    id: 'notifyTheStudent'
                                }
                            )}
                        </label>
                    </div>
                    <hr />
                    <FormattedMessage
                        id={"tooltip.errorMessage"}
                        defaultMessage={
                            "You can also report an error from the button below, for instance, if the request should be targeted to another person."
                        }
                        values={{ br: <br /> }}
                    />
                </div> :
                <>
                    <FormattedMessage
                        id={"tooltip.markAsSeen.smsDisabled"}
                        defaultMessage={
                            "Mark the request as seen and proceed with support actions.{br}{br}You can also report an error from the button below, for instance, if the request should be targeted to another person."
                        }
                        values={{ br: <br /> }}
                    />
                </>
            }
        </Tooltip>
    </div>
        <hr />
        <button className={"main tertiary"} onClick={()=>goToReportError()}><ErrorIcon />
            {intl.formatMessage({
                id: 'reportError',
                defaultMessage: 'Report error',
            })}
        </button>
    </>
}

function SendMessage({goBack, goToNextView, sendStudentMessage, userRole, userFirstName, userLastName, messagesData}) {
    const [messageContent, setMessageContent] = useState("")
    const {setSnackbarData} = useSnackbar()

    const intl = useIntl()
    const [loading, setLoading] = useState(false)

    const successMessage = intl.formatMessage({id:"smsSuccess",defaultMessage:"Message Sent!"})
    const errorMessage = intl.formatMessage({id:"failedSentMessage",defaultMessage:"Failed to send message. Please try again later."})

    const sendMessage = () => {
        setLoading(true)
        const metaForSms = userRole?.length > 0 && userFirstName?.length > 0 && userLastName?.length > 0

        const annieuser = sanitizeTemplateVar(userRole + userFirstName + userLastName)
        const content = sanitizeTemplateVar(messageContent)
        const activeTpl = messagesData.messageTemplate
        const passiveTpl = messagesData.messageTemplatePassive

        let smsContentForMessage
        if (metaForSms && annieuser && activeTpl) {
            smsContentForMessage = activeTpl.replace("{annieuser}", annieuser).replace("{messageContent}", content)
        } else if (passiveTpl) {
            smsContentForMessage = passiveTpl.replace("{messageContent}", content)
        } else {
            // Legacy fallback when no template is configured (still newline-safe)
            smsContentForMessage = (annieuser ? annieuser + " " : "") + content
        }

        new Promise((resolve, reject) => {
            sendStudentMessage(smsContentForMessage, resolve, reject)
        }).then(() => {
            setMessageContent("")
            setLoading(false)
            setSnackbarData({
                show: true,
                text: successMessage,
                state: "success",
                autoHide: true,
            })
            goToNextView()
        }).catch( error => {
            console.log(error)
            setLoading(false)
            setSnackbarData({
                show: true,
                text: errorMessage,
                state: "error",
                autoHide: true,
            })
        })

    }

    return <div className={"error-report"}>
        <div className={"header-container"}>
            <div>
                <button className={"back no-background"} onClick={()=>{
                    goBack()
                }}>
                    <BackArrow />{intl.formatMessage({id:"back", defaultMessage:"Back"})}
                </button>
            </div>
            <h4>
                {intl.formatMessage({
                    id: 'sendMessageToStudent',
                    defaultMessage: 'Message Student',
                })}
            </h4>
        </div>

        <input type={"text"} className={"message-input"} placeholder={intl.formatMessage({defaultMessage:"Write your message to the student here.", id:"sendMessagePlaceholder"})} onChange={(e)=>setMessageContent(e.target.value.replace(/[\r\n]+/g, " "))} value={messageContent} maxLength={WHATSAPP_MESSAGE_MAX} autoFocus={true}/>
        {messageContent.length >= WHATSAPP_MESSAGE_MAX &&
            <div className={"message-counter at-limit"}>{messageContent.length}/{WHATSAPP_MESSAGE_MAX}</div>
        }
        {messageContent.length > 0 ?
            (loading ?
                    <button className={"main secondary"} disabled ><LoadingIcon />
                        {intl.formatMessage({
                            id: 'sendingMessage',
                            defaultMessage: 'Sending Message...',
                        })}
                    </button> :
                <button className={"main secondary"} onClick={()=>sendMessage()}><MessageIcon />
                    {intl.formatMessage({
                        id: 'sendMessage',
                        defaultMessage: 'Send Message',
                    })}
                </button>
                )
             :
            <button className={"main secondary"} disabled title={intl.formatMessage({defaultMessage:"Please write the message before sending.", id:"sendMessageErrorMessage"})}><MessageIcon />
                {intl.formatMessage({
                    id: 'sendMessage',
                    defaultMessage: 'Send Message',
                })}
            </button>
        }
    </div>
}

function SendErrorReport({goBack, postComment, goToErrorCompliment, postSupportNeed, request, nameAndUserId}) {
    const [errorMessageContent, setErrorMessageContent] = useState("")

    const intl = useIntl()
    const postSendEmail = PostSendEmail()
    const {setSnackbarData} = useSnackbar()

    const [loading, setLoading] = useState(false)

    const setStatusError = () => {
        const supportNeedUpdate = {
            "contact": request?.contact,
            "survey": request?.survey,
            "category": request?.category,
            "status": "ERROR",
            "supporttype": request?.supporttype,
            "followuptype": request?.followuptype,
            "followupresult": request?.followupresult,
            "followupstatus": request?.followupstatus
        }
        postSupportNeed.mutate(supportNeedUpdate, {
            onError: error => {
                console.log(error)
                setLoading(false)
                setSnackbarData({
                    show: true,
                    text: intl.formatMessage({id:"errorStatusChangeFailed",defaultMessage:"Changing status to error. Please try again later."}),
                    state: "error",
                    autoHide: true,
                })
            },
            onSuccess:() => {
                console.log("Status changed to error")
                const postErrorTextAsComment = new Promise( (resolve, reject) => {
                    postComment(errorMessageContent, resolve, reject)
                })
                postErrorTextAsComment.then(()=> {
                    setErrorMessageContent("")
                    setLoading(false)
                    setSnackbarData({
                        show: true,
                        text: intl.formatMessage({id:"errorReportSentSuccess",defaultMessage:"Sending error report was successful."}),
                        state: "success",
                        autoHide: true,
                    })
                    goToErrorCompliment()
                }).catch(error => {
                    console.log(error)
                    setSnackbarData({
                        show: true,
                        text: intl.formatMessage({id:"addingErrorCommentFailed",defaultMessage:"Adding error comment failed. Please try again later."}),
                        state: "error",
                        autoHide: true,
                    })
                })
            }
        })
    }

    const sendErrorMessageAndSetStatusError = () => {
        setLoading(true)
        const emailData = {
            "subject": "[ID: "+request.id+"] New error report from "+nameAndUserId,
            "body": "<h1>Error Message</h1>" +
                "<p><em>" + errorMessageContent + "</em></p>" +
                "<table>" +
                "<tr><th>RequestId</th><td>" + request?.id + "</td></tr>" +
                "<tr><th>Student</th><td>" + request?.studentName + "</td></tr>" +
                "<tr><th>ContactId</th><td>" + request?.contact + "</td></tr>" +
                "<tr><th>Client</th><td>" + request?.client + "</td></tr>" +
                "<tr><th>SurveyId</th><td>" + request?.survey + "</td></tr>" +
                "<tr><th>Reporter</th><td>" + nameAndUserId + "</td></tr>" +
                "</table>"
        }
        postSendEmail.mutate(emailData, {
            onError: error => {
                console.log(error)
                setLoading(false)
                setSnackbarData({
                    show: true,
                    text: intl.formatMessage({id:"errorReportFail",defaultMessage:"Sending error report failed. Please try again later."}),
                    state: "error",
                    autoHide: true,
                })
            },
            onSuccess: () => {
                console.log("Email sent")
                setStatusError()
            }
        })
    }

    return <div className={"error-report"}>
        <div className={"header-container"}>
            <div>
                <button className={"back no-background"} onClick={()=>{
                    goBack()
                }}>
                    <BackArrow />{intl.formatMessage({id:"back", defaultMessage:"Back"})}
                </button>
            </div>
            <h4>
                {intl.formatMessage({
                    id: 'reportError',
                    defaultMessage: 'Report error',
                })}
            </h4>
        </div>

        <textarea placeholder={intl.formatMessage({defaultMessage:"Please describe the error or issue you encountered.", id:"errorReportPlaceholder"})} onChange={(e)=>setErrorMessageContent(e.target.value)} value={errorMessageContent} autoFocus={true}/>
        {loading ?
            <>
                <button className={"main secondary"} disabled>
                    <LoadingIcon />
                    {intl.formatMessage({
                        id: 'sendingErrorMessage',
                        defaultMessage: 'Sending report..',
                    })}
                </button>
            </> :
            <>
                {errorMessageContent.length > 0 ?
                    <button className={"main secondary"} onClick={()=>sendErrorMessageAndSetStatusError()}><MailIcon />
                        {intl.formatMessage({
                            id: 'sendErrorMessage',
                            defaultMessage: 'Send error message',
                        })}
                    </button> :
                    <button className={"main secondary"} disabled title={intl.formatMessage({defaultMessage:"Describe the error before sending", id:"errorReportErrorMessage"})}><MailIcon />
                        {intl.formatMessage({
                            id: 'sendErrorMessage',
                            defaultMessage: 'Send error message',
                        })}
                    </button>
                }
            </>
        }

    </div>
}

function AddNotes({comments, goToErrorDialog, goToMessageDialog, goToCommentCompliment, postComment, smsEnabled, sendStudentSms, userRole, userFirstName, userLastName, showSkipButtonAfterSeen}) {
    const intl = useIntl()
    const [showCustomComment, setShowCustomComment] = useState(false)
    const [selectedComments, setSelectedComments] = useState([])
    const [customCommentContent, setCustomCommentContent] = useState("")
    const {setSnackbarData} = useSnackbar()

    const [loading, setLoading] = useState(false)

    const postSelectedComments = () => {
        setLoading(true)
        const customComment = customCommentContent.length > 0
        const multipleComments = customComment ? selectedComments?.length > 0 : selectedComments?.length > 1
        if (customComment) {
            selectedComments.push(customCommentContent)
        }
        const commentPosts = selectedComments.reduce((postChain, comment) => {
            return postChain.then(() => new Promise((resolve, reject) => {
                postComment(comment, resolve, reject)
            }))
        }, Promise.resolve())

        const successMessage = multipleComments ?
            intl.formatMessage({id:"addingNotesSuccess",defaultMessage:"Notes added!"}) :
                intl.formatMessage({id:"addingNoteSuccess",defaultMessage:"Note added!"})

        const errorMessage = multipleComments ?
            intl.formatMessage({id:"addingNotesFailed",defaultMessage:"Adding notes failed. Please try again later."}) :
            intl.formatMessage({id:"addingNoteFailed",defaultMessage:"Adding note failed. Please try again later."})

        commentPosts.then(() => {
            setLoading(false)
            setSnackbarData({
                show: true,
                text: successMessage,
                state: "success",
                autoHide: true,
            })
            setSelectedComments([])
            setShowCustomComment(false)
            setCustomCommentContent("")
            goToCommentCompliment()
        }).catch(()=> {
            setLoading(false)
            setSnackbarData({
                show: true,
                text: errorMessage,
                state: "error",
                autoHide: true,
            })
        })
    }

    const toggleCommentSelection = (option, i) => {
        if (selectedComments.includes(option)) {
            setSelectedComments(selectedComments.filter(comment => comment !== option))
        } else {
            setSelectedComments(selectedComments => [...selectedComments, option])
        }
    }

    const customCommentRef = useRef(null)

    useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside)

        function handleClickOutside(event) {
            if (customCommentRef.current && !customCommentRef.current.contains(event.target)) {
                if (showCustomComment && customCommentRef.current.value.length < 1) {
                    setShowCustomComment(false)
                }
            }
            return () => {
                document.removeEventListener("mousedown", handleClickOutside);
            }
        }
    },[showCustomComment, customCommentContent, customCommentRef])

    return <>
        <h4>
            {intl.formatMessage({
                id: 'selectNotesToAdd',
                defaultMessage: 'Select notes to add'
            })}
            <Tooltip>
                <FormattedMessage
                    id={"tooltip.whatNext"}
                    defaultMessage={"Add a note describing what happens next with the support request.{br}The student can't see the notes.{br}You can choose multiple options or write your own."}
                    values={{br: <br/>}}/>
            </Tooltip>
        </h4>
        <div className={"comment-options button-group"} role={"group"} aria-label={"Checkbox toggle button group"}>
            {comments.map((option, i) => {
                return <React.Fragment key={i}>
                    <input type={"checkbox"} id={"checkbox"+i} name={"checkbox"+i} checked={selectedComments.includes(option)} onChange={()=>toggleCommentSelection(option,i)}/>
                    <label className={"button round-button"} htmlFor={"checkbox"+i}>{option}</label>
                </React.Fragment>
            })}
            {showCustomComment ?
                <input type={"text"} ref={customCommentRef} className={"custom-comment"} placeholder={intl.formatMessage({id:'writeNote',defaultMessage:'Write note'})} autoFocus={true} value={customCommentContent} onChange={e=>setCustomCommentContent(e.target.value)} />
                :
                <button className={"round-button add-comment"} onClick={()=>setShowCustomComment(true)}>
                    {intl.formatMessage({
                        id: 'writeYourOwn',
                        defaultMessage: 'Write a custom note',
                    })}
                    <PenIcon /></button>
            }
        </div>
        {(selectedComments.length > 0 || customCommentContent.length > 0) ?
            (loading ?
                    <button className={"main secondary"} disabled><LoadingIcon />
                        {intl.formatMessage({
                            id: 'addingNote',
                            defaultMessage: 'Adding note...',
                        })}
                    </button>:
                    <button className={"main secondary"} onClick={()=>postSelectedComments()}><CommentIcon />
                        {intl.formatMessage({
                            id: 'addComments',
                            defaultMessage: 'Add note',
                        })}
                    </button>
            ) :
            <button className={"main secondary"} title={intl.formatMessage({id:"selectNotesBeforeAdding",defaultMessage:"Please select the notes you wish to add."})} disabled><CommentIcon />
                {intl.formatMessage({
                    id: 'addComments',
                    defaultMessage: 'Add note',
                })}
            </button>
        }
        <hr />
        {smsEnabled &&
            <button className={"main tertiary"} onClick={()=>goToMessageDialog()}><MessageIcon />
                {intl.formatMessage({
                    id: 'sendMessageToStudent',
                    defaultMessage: 'Message Student',
                })}
            </button>
        }
        <button className={"main tertiary"} onClick={()=> {
            goToErrorDialog()
        }}><ErrorIcon />
            {intl.formatMessage({
                id: 'reportError',
                defaultMessage: 'Report error',
            })}
        </button>
        <button className={"no-background"} onClick={()=> {
            goToCommentCompliment()
        }}>
            {intl.formatMessage({
                id: 'skip',
                defaultMessage: 'Skip',
            })}
        </button>
    </>
}

function FinishedView({goToView, activeCompliment, smsEnabled}) {
    const intl = useIntl()
    return <>
        <div className={"high-five-container"}>
            {activeCompliment === "error" ?
                <>
                    <div id={"error-boy"}>
                        <ErrorBoy />
                    </div>
                    <h4>
                        <FormattedMessage
                            id={"thankYouForErrorMessage"}
                            defaultMessage={"Thank you for reporting an error.{br}We'll check the situation."}
                            values={{br: <br/>}}/>
                    </h4>
                </> :
                <>
                    <div id={"high-five"}>
                        <HighFive />
                    </div>
                    <h4>
                        {intl.formatMessage({
                            id: 'taskCompleted',
                            defaultMessage: "That's that for this request!",
                        })}
                    </h4>
                </>
            }
        </div>
        <ButtonToRequestsOrGroup />
        <hr />
        <button className={"main tertiary stroke"} onClick={()=>{
            goToView("comment")
        }}><CommentIcon />
            {intl.formatMessage({
                id: 'addComments',
                defaultMessage: 'Add note',
            })}
        </button>
        {smsEnabled &&
            <button className={"main tertiary"} onClick={()=>goToView("message")}><MessageIcon />
                {intl.formatMessage({
                    id: 'sendMessageToStudent',
                    defaultMessage: 'Message Student',
                })}
            </button>
        }
        <ButtonToRequestsOrGroup noBackground={true} />
    </>
}

function ErrorCompliment({goToAddComments, hideSkipButton}) {
    const intl = useIntl()
    return <>
        <div className={"high-five-container"}>
            <div id={"error-boy"}>
                <ErrorBoy />
            </div>
            <h4>
                <FormattedMessage
                    id={"thankYouForErrorMessage"}
                    defaultMessage={"Thank you for reporting an error.{br}We'll check the situation."}
                    values={{br: <br/>}}/>
            </h4>
        </div>
        <ButtonToRequestsOrGroup />
        <button className={"no-background"} onClick={()=>{
            goToAddComments()
            hideSkipButton()
        }}>
            {intl.formatMessage({
                id: 'addMoreComments',
                defaultMessage: '+ Add notes',
            })}
        </button>
    </>
}

function SupportRequestSkeleton() {
    return <div className={"support-request skeleton-container"}>
        <div className={"support-request-header"}>
            <div>
                <Skeleton width={140} height={18} marginBottom={12}/>
                <Skeleton width={100} height={22} marginBottom={10}/>
                <Skeleton width={120} height={17}/>
            </div>
        </div>
        <div className={"student-info"}>
            <div>
                <table>
                    <thead>
                    <tr>
                        <th>
                            <Skeleton width={60} height={24}/>
                        </th>
                        <th>
                            <Skeleton width={60} height={24}/>
                        </th>
                        <th>
                            <Skeleton width={60} height={24}/>
                        </th>
                        <th>
                            <Skeleton width={60} height={24}/>
                        </th>
                        <th>
                            <Skeleton width={60} height={24}/>
                        </th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr>
                        <td>
                            <Skeleton width={120} height={17}/>
                        </td>
                        <td>
                            <Skeleton width={120} height={17}/>
                        </td>
                        <td>
                            <Skeleton width={120} height={17}/>
                        </td>
                        <td>
                            <Skeleton width={120} height={17}/>
                        </td>
                        <td>
                            <Skeleton width={120} height={17}/>
                        </td>
                    </tr>
                    </tbody>
                </table>
            </div>
        </div>
        <div className={"request-history-container"}>
            <div className={"request-history"}>
                <div className={"request-message date"}>
                    <Skeleton width={70} height={14}/>
                </div>
                <div className={"request-message message annie"}>
                    <Skeleton width={100} height={14}/>
                    <Skeleton width={390} height={14}/>
                    <Skeleton width={200} height={14} marginBottom={14}/>
                    <Skeleton width={20} height={14}/>
                    <Skeleton width={70} height={14}/>
                </div>
                <div className={"request-message message"}>
                    <Skeleton width={100} height={14}/>
                    <Skeleton width={50} height={14}/>
                </div>
                <div className={"request-message message annie"}>
                    <Skeleton width={100} height={14}/>
                    <Skeleton width={390} height={14}/>
                </div>
                <div className={"request-message system"}>
                    <Skeleton width={100} height={14}/>
                    <Skeleton width={180} height={14}/>
                </div>
            </div>
        </div>
        <div className={"request-actions"}>
            <div className={"request-actions-container"}>
                <button className={"main"}>
                    <Skeleton width={200} height={20} />
                </button>
                <button className={"no-background"}>
                    <Skeleton width={200} height={20} />
                </button>
            </div>
        </div>
    </div>
}

function NoRightsForRequest() {
    const intl = useIntl()
    useEffect(() => {
        document.title = "401 | Annie Advisor"
    }, [])

    return <div className={"404"}>
        <h1>
            {intl.formatMessage({
                id: 'requestNotFoundHeader',
                defaultMessage: 'Couldnt access support request'
            })}
        </h1>
        <p>
            {intl.formatMessage({
                id: 'requestNotFoundText',
                defaultMessage: 'Either you dont have the rights to view this support request or it doesnt exist.'
            })}
        </p>
    </div>
}

function NoRequestWithId() {
    const intl = useIntl()
    useEffect(() => {
        document.title = "404 | Annie Advisor"
    }, [])

    return <div className={"404"}>
        <h1>
            {intl.formatMessage({
                id: 'requestNotFoundHeader',
                defaultMessage: 'Couldnt access support request'
            })}
        </h1>
        <p>
            {intl.formatMessage({
                id: 'requestNotFoundText',
                defaultMessage: 'Either you dont have the rights to view this support request or it doesnt exist.'
            })}
        </p>
    </div>
}

function FailedToLoad() {
    const intl = useIntl()
    useEffect(() => {
        document.title = "Error | Annie Advisor"
    }, [])

    return <div className={"404"}>
        <h1>
            {intl.formatMessage({
                id: 'failedToLoadRequestHeader',
                defaultMessage: 'Failed to load support request'
            })}
        </h1>
        <p>
            {intl.formatMessage({
                id: 'failedToLoadRequestText',
                defaultMessage: 'Loading data for support request failed. Please try reloading.'
            })}
        </p>
    </div>
}

function StudentInfo({student, columnConfig}) {
    const intl = useIntl()
    const defaultColumns = Object.keys(columnConfig).filter(key => columnConfig[key]?.appVisible && !(key === 'firstname' || key === 'lastname')).sort((a,b)=>columnConfig[a]?.order - columnConfig[b]?.order)
    const [studentInfo, setStudentInfo] = useState(
        localStorage.getItem("contactMetaSelections") ?
            JSON.parse(localStorage.getItem("contactMetaSelections")) :
        defaultColumns
    )

    const textTelOrMailto = e => e === "phonenumber" ? <a href={"tel:"+student[e]}>{student[e]}</a> : e === "email" ? <a href={"mailto:"+student[e]}>{student[e]}</a> : student[e]

    return <>
        <div className={"student-info"}>
            <div>
                <div className={"table"}>
                    {studentInfo.sort((a,b)=>columnConfig[a]?.i - columnConfig[b]?.i).map((e,i) => {
                        if (student.hasOwnProperty(e)) {
                            return <div key={i}>
                                <div className={"th"}>
                                    {intl.formatMessage({
                                        id: `contactmeta.${e}`,
                                        defaultMessage: e
                                    })}
                                </div>
                                <div className={"td"}>{student[e] ? textTelOrMailto(e) :
                                    <em>{intl.formatMessage({
                                        id: 'missingInfo',
                                        defaultMessage: 'Missing info',
                                    })}</em>}</div>
                            </div>
                        }
                        return null
                    })}
                </div>
                <StudentInfoSelector studentContact={student || {}}
                                     columnConfig={columnConfig}
                                     setInfoColumns={(e)=>setStudentInfo(e)}
                                     defaultColumns={defaultColumns}
                />
            </div>
        </div>
    </>
}

function StudentInfoSelector({studentContact, columnConfig, setInfoColumns, defaultColumns}) {
    const [showSelectorPopover, setShowSelectorPopover] = useState(false)
    const [selectedColumns, setSelectedColumns] = useState(
        localStorage.getItem("contactMetaSelections") ?
            JSON.parse(localStorage.getItem("contactMetaSelections")) :
            defaultColumns)
    const popoverRef = useRef(null)
    const popoverTriggerRef = useRef(null)
    const intl = useIntl()

    const toggleColumnSelection = column => {
        if (selectedColumns.includes(column)) {
            setSelectedColumns(selectedColumns.filter(selected => selected !== column))
        } else {
            setSelectedColumns( selectedColumns => [...selectedColumns, column])
        }
    }

    const cancel = () => {
        setSelectedColumns(localStorage.getItem("contactMetaSelections") ?
            JSON.parse(localStorage.getItem("contactMetaSelections")) :
            defaultColumns)
        setShowSelectorPopover(false)
    }

    const select = () => {
        localStorage.setItem("contactMetaSelections", JSON.stringify(selectedColumns))
        setInfoColumns(selectedColumns)
        setShowSelectorPopover(false)
    }

    const restoreDefaults = () => {
        localStorage.removeItem("contactMetaSelections")
        setInfoColumns(defaultColumns)
        setSelectedColumns(defaultColumns)
        setShowSelectorPopover(false)
    }

    useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside)
        function handleClickOutside(event) {
            if (popoverRef.current && !popoverRef.current.contains(event.target) && !popoverTriggerRef.current.contains(event.target) ) {
                cancel()
            }
            return () => {
                document.removeEventListener("mousedown", handleClickOutside);
            }
        }
    },[popoverRef])

    const contactMetaSelections = Object.keys(columnConfig).filter(obj => columnConfig[obj]?.appVisible).sort((a,b)=>columnConfig[a]?.order - columnConfig[b]?.order)

    return <div className={"selector-container"}>
        <button className={"icon-button"} ref={popoverTriggerRef} onClick={()=>setShowSelectorPopover(!showSelectorPopover)}>
            <MoreIcon />
        </button>
        {showSelectorPopover &&
            <Popup header={intl.formatMessage({
                id: 'show',
                defaultMessage: 'Show',
            })}
                   onClose={()=>cancel()}
                   className={"selector-popover"}
            >
                <ul>
                    {contactMetaSelections.map((column, i) => {
                        return <li key={i} className={!studentContact.hasOwnProperty(column) ? "no-info" : ""}>
                            <input type={"checkbox"} id={"column"+i} name={"column"+i} checked={selectedColumns.includes(column)} onChange={()=>toggleColumnSelection(column,i)} />
                            <label htmlFor={"column"+i}>
                                {intl.formatMessage({
                                    id: column,
                                    defaultMessage: column
                                })}
                            </label>
                        </li>
                    })}
                </ul>
                <div className={"options"}>
                    <button onClick={()=>restoreDefaults()}>{intl.formatMessage({
                        id: 'restoreDefaults',
                        defaultMessage: 'Restore Defaults',
                    })}</button>
                    <button onClick={()=>cancel()}>{intl.formatMessage({
                        id: 'cancel',
                        defaultMessage: 'Cancel',
                    })}</button>
                    <button className="select" onClick={()=>select()}>{intl.formatMessage({
                        id: 'select',
                        defaultMessage: 'Select',
                    })}</button>
                </div>
            </Popup>
        }
    </div>
}

function RequestHistory({request, codes, requestComments, requestHistory, userData}) {
    const getRequestMessages = GetRequestMessages(request.contact)
    const error = getRequestMessages.status === "error"
    const loading = getRequestMessages.status === "loading"
    const success = getRequestMessages.status === "success"

    return <>
        <div className={"request-history-container"}>
            {error &&
                <h2>Error in loading request history</h2>
            }
            {loading &&
                <h2>Loading history</h2>
            }
            {success &&
            <RequestTimeline codes={codes}
                             requestMessages={getRequestMessages.data.filter(message => message.survey === request.survey)}
                             requestComments={requestComments}
                             requestHistory={requestHistory}
                             userData={userData}
            />
            }
        </div>
    </>
}

function RequestTimeline({codes, requestMessages, requestComments, requestHistory, userData}) {
    requestMessages.forEach(message => message.type = "message")
    requestComments.forEach(comment => comment.type = "comment")
    requestHistory.forEach(history => history.type = "system")
    const requestTimelineData = requestMessages.concat(requestComments, requestHistory)
    const sortedTimelineData = requestTimelineData.sort((a,b) => new Date(formatDate(a.updated)) - new Date(formatDate(b.updated)))
    const messagesLatestRef = useRef(null)
    const timelineRef = useRef(null)
    const scrollToLatest = () => {
        // messagesLatestRef.current?.scrollIntoView({block: 'end', inline: 'start', behavior: 'smooth'})
        timelineRef.current.scrollTop = timelineRef.current.scrollHeight
    }
    useEffect(() =>{
        scrollToLatest()
    }, [requestComments.length, requestMessages.length, requestHistory.length])

    return <>
        <div className={"request-history"} ref={timelineRef}>
            {sortedTimelineData.map((e,i) => {
                return <RequestMessage
                    request={e} codes={codes}
                    previousRequest={i !== 0 ? sortedTimelineData[i-1] : null} key={i}
                    systemRequests={requestHistory.sort((a,b) => new Date(formatDate(a.updated)) - new Date(formatDate(b.updated)))}
                    systemIndex={requestHistory.sort((a,b) => new Date(formatDate(a.updated)) - new Date(formatDate(b.updated))).indexOf(e)}
                    userData={userData}
                />
            })}
            <div className={"scroll"} ref={messagesLatestRef} />
        </div>
    </>
}

function RequestMessage({request, codes, previousRequest, systemRequests, systemIndex, userData}){
    const intl = useIntl()
    const isMessage = request.type === "message"
    const isSystemMessage = request.type === "system"
    const updated = new Date(formatDate(request.updated)).toLocaleString([intl.locale], {hour: '2-digit', minute:'2-digit'})
    const previousUpdated = previousRequest ? new Date(formatDate(previousRequest.updated)).toLocaleString([intl.locale], {hour: '2-digit', minute:'2-digit'}) : null
    const updateDate = new Date(formatDate(request.updated)).toLocaleString([intl.locale],
        {weekday: "short",
            year: "numeric",
            month: "2-digit",
            day: "numeric"})
    const previousUpdateDate = previousRequest ? new Date(formatDate(previousRequest.updated)).toLocaleString([intl.locale],
        {weekday: "short",
            year: "numeric",
            month: "2-digit",
            day: "numeric"}) : null
    const updatedBy = isMessage ? request.sender : findUserById(request?.updatedby, userData, intl)
    const messageType = isMessage ? request.sender === "Annie" ? "message annie" : "message" : isSystemMessage && request.status === "ACKED" ? request.type + " seen" : isSystemMessage && request.status === "ERROR" ? request.type + " error" : request.type
    const previousSystemRequest = isSystemMessage && systemIndex > 0 ? systemRequests[systemIndex - 1] : null
    const previousStatus = previousSystemRequest?.status
    const previousFollowupStatus = previousSystemRequest?.followupstatus
    const showFollowupUpdate = systemIndex > 0 && request?.followupstatus !== previousFollowupStatus && (previousFollowupStatus === null || request?.followupstatus === "ACKED")
    const requestStatusName = (status) => {
        if (status === "ACKED") {
            return intl.formatMessage({
                id: 'seen',
                defaultMessage: 'Seen',
            })
        }
        if (status === "OPENED") {
            return intl.formatMessage({
                id: 'open',
                defaultMessage: 'Open',
            })
        }
        if (status === "NEW") {
            return intl.formatMessage({
                id: 'new',
                defaultMessage: 'New',
            })
        }
        return status
    }
    const requestText = isSystemMessage ?
        previousSystemRequest && request.category !== previousSystemRequest.category ?
            <>{intl.formatMessage({
                id: 'changedSubjectTo',
                defaultMessage: 'Changed subject to:',
            })} <em>{codes.hasOwnProperty([request.category]) ? getTopicDisplayName(request.category, codes[request.category], intl) : intl.formatMessage({
                id: 'missingSubject',
                defaultMessage: 'Missing subject',
            })}</em></> :
            systemIndex === 0 ?
                intl.formatMessage({
                    id: 'supportRequestCreated',
                    defaultMessage: 'Support request created',
                }) :
                request.status === "ACKED" ?
                    intl.formatMessage({
                        id: 'markedRequestSeen',
                        defaultMessage: 'Marked request seen',
                    }) :
                    request.status === "ERROR" ?
                        intl.formatMessage({
                            id: 'reportedAnError',
                            defaultMessage: 'Reported an error',
                        }) :
                        <>{intl.formatMessage({
                            id: 'changedStatusTo',
                            defaultMessage: 'Changed status to:',
                        }) + ' '}<em>{requestStatusName(request.status)}</em></> :
        null
    const statusChangeToOpen = isSystemMessage && request.status === "OPENED"
    const hideRequestUpdate = statusChangeToOpen || (isSystemMessage && previousStatus === request?.status)
    const showUpdateDate = (updateDate !== previousUpdateDate) && (!hideRequestUpdate || showFollowupUpdate)
    const dateDifferenceUnderHour = previousRequest && (Math.abs(new Date(formatDate(request.updated)) - (new Date(formatDate(previousRequest.updated)))) < 3600000)
    const showUpdateTime = !showUpdateDate && !dateDifferenceUnderHour && (!hideRequestUpdate || showFollowupUpdate)

    return <>
        {showUpdateDate &&
        <div className={"request-message date"}>
            <p>{updateDate} {updated}</p>
        </div>
        }
        {showUpdateTime &&
        <div className={"request-message date"}>
            <p>{updated}</p>
        </div>
        }
        {!hideRequestUpdate &&
        <div className={"timeline-item " + messageType}>
            {isMessage &&
            <div className={"user-icon"}>
                <UserIcon />
            </div>
            }
            <div className={"item-container"}>
                <p className={"sender"}>
                    {updatedBy}
                    {messageType === "comment" &&
                    " " + intl.formatMessage({
                        id: 'addedNote',
                        defaultMessage: 'added note',
                    })+ ":"}
                </p>
                <div className={"request-message "+messageType}>
                    {isSystemMessage ?
                        <p className={"system-text"}>
                            {requestText}
                        </p> :
                        <p className={"text-body"}>
                            {request.body}
                        </p>
                    }
                </div>
            </div>
        </div>
        }
    </>
}

function useSupportData(requestId) {
    const getSupportNeed = GetSupportNeed(requestId);
    const getCodes = GetCodes();
    const getComments = GetCommentOptions();
    const getColumnConfig = GetColumnConfig();
    const getRequestComments = GetRequestComments(requestId);
    const getSMSSendEnabled = GetSMSSendEnabled();
    const getRequestSeenSmsEnabled = GetRequestSeenSms();
    const getAPIRoot = GetAPIRoot()

    const loading = getSupportNeed.isLoading || getCodes.isLoading || getComments.isLoading || getColumnConfig.isLoading || getRequestComments.isLoading || getSMSSendEnabled.isLoading || getRequestSeenSmsEnabled.isLoading || getAPIRoot.isLoading;
    const error = getSupportNeed.isError || getCodes.isError || getComments.isError || getColumnConfig.isError || getRequestComments.isError || getSMSSendEnabled.isError || getRequestSeenSmsEnabled.isError || getAPIRoot.isError;
    const success = getSupportNeed.isSuccess && getCodes.isSuccess && getComments.isSuccess && getColumnConfig.isSuccess && getRequestComments.isSuccess && getSMSSendEnabled.isSuccess && getRequestSeenSmsEnabled.isSuccess && getAPIRoot.isSuccess;

    return {
        getAPIRoot,
        getSupportNeed,
        getCodes,
        getComments,
        getColumnConfig,
        getRequestComments,
        getSMSSendEnabled,
        getRequestSeenSmsEnabled,
        loading,
        error,
        success,
    }
}

function useContactAndSurvey(supportNeedData) {
    const [contactId, setContactId] = useState(null);
    const getContact = GetContact(contactId, contactId !== null);

    const [surveyId, setSurveyId] = useState(null);
    const getSurvey = GetSurveyWithId(surveyId, surveyId !== null);

    useEffect(() => {
        if (supportNeedData.success) {
            const request = supportNeedData.getSupportNeed?.data.reduce((max, obj) => (obj.id > max.id ? obj : max), { id: -Infinity });
            if (request?.contact !== contactId) {
                setContactId(request?.contact);
            }
            if (request?.survey !== surveyId) {
                setSurveyId(request?.survey);
            }
        }
    }, [supportNeedData, contactId, surveyId]);

    return { getContact, getSurvey, contactId, surveyId, setContactId, setSurveyId };
}

function useRequestWithId(requestId) {
    const supportData = useSupportData(requestId);
    const { getContact, getSurvey, contactId, surveyId } = useContactAndSurvey(supportData);

    const { instanceLang } = useLanguageData();
    const [lang, setLang] = useState(null);

    useEffect(() => {
        if (surveyId && getSurvey.isSuccess) {
            if (!lang) {
                const surveyLanguage = getSurvey.data[0]?.config?.language || instanceLang;
                setLang(surveyLanguage);
            }
        }
    }, [getSurvey.data, surveyId, getSurvey.isSuccess, lang]);

    const getProviderHasSeenYourMessageText = GetProviderHasSeenYourMessageText(lang, lang !== null);
    const getMessageSeenText = GetMessageSeenText(lang, lang !== null);
    const getSentYouAMessage = GetSentYouAMessage(lang, lang !== null);
    const getMessageWasSentToYou = GetMessageWasSentToYou(lang, lang !== null);
    const getMessageTemplate = GetMessageTemplate(lang, lang !== null);
    const getMessageTemplatePassive = GetMessageTemplatePassive(lang, lang !== null);

    return {
        ...supportData,
        getContact,
        getSurvey,
        contactId,
        surveyId,
        getProviderHasSeenYourMessageText,
        getMessageSeenText,
        getSentYouAMessage,
        getMessageWasSentToYou,
        getMessageTemplate,
        getMessageTemplatePassive,
        lang,
        setLang
    };
}

function GetRequestWithId(requestId) {
    const intl = useIntl();

    const {
        getAPIRoot,
        getSupportNeed,
        getCodes,
        getComments,
        getColumnConfig,
        getRequestComments,
        getSMSSendEnabled,
        getRequestSeenSmsEnabled,
        loading,
        error,
        success,
        getContact,
        getSurvey,
        contactId,
        surveyId,
        getProviderHasSeenYourMessageText,
        getMessageSeenText,
        getSentYouAMessage,
        getMessageWasSentToYou,
        getMessageTemplate,
        getMessageTemplatePassive,
        lang,
        setLang
    } = useRequestWithId(requestId);

    const unAuthorized = error && getSupportNeed.error?.response?.status === 401;

    const messagesLoading = getProviderHasSeenYourMessageText.isLoading || getMessageSeenText.isLoading || getSentYouAMessage.isLoading || getMessageWasSentToYou.isLoading
    const messagesLoaded = getProviderHasSeenYourMessageText.isSuccess && getMessageSeenText.isSuccess && getSentYouAMessage.isSuccess && getMessageWasSentToYou.isSuccess

    if (error || getContact.isError || getSurvey.isError) {
        if (unAuthorized) {
            return { status: "401", data: {} };
        }
        return { status: "error", data: {} };
    }

    if (loading || messagesLoading) {
        return { status: "loading", data: {} };
    }

    if (success && messagesLoaded) {
        if (getSupportNeed.data.length === 0) {
            return { status: "404", data: {} };
        } else {
            let request = getSupportNeed.data.reduce((max, obj) => (obj.id > max.id ? obj : max), { id: -Infinity });

            if (getContact.isSuccess && contactId && getSurvey.isSuccess && surveyId) {
                const contact = getContact.data[0];
                request.contactmeta = contact?.meta;
                request.studentName = request?.contactmeta.hasOwnProperty("firstname") && request?.contactmeta.hasOwnProperty("lastname")
                    ? request.contactmeta.firstname + " " + request.contactmeta.lastname
                    : request?.contactmeta
                        ? intl.formatMessage({ id: "missingName", defaultMessage: "Missing name" })
                        : intl.formatMessage({ id: "dataExpired", defaultMessage: "Student info expired" });

                const survey = getSurvey.data[0];
                const surveyLanguage = survey?.config?.language ? survey.config.language : lang;
                request.surveyLanguage = surveyLanguage;
                request.categoryName = getCodes.data[0].hasOwnProperty(request.category) ? getTopicDisplayName(request.category, getCodes.data[0][request.category], intl) : null;
                request.client = getAPIRoot?.data?.client || "Missing client info"

                const messagesObj = {
                    hasSeenYourMessageAndWillContact: getProviderHasSeenYourMessageText?.data[0]?.value,
                    messageSeenAndWillContact: getMessageSeenText?.data[0]?.value,
                    sentYouAMessage: getSentYouAMessage?.data[0]?.value,
                    aMessageWasSentToYou: getMessageWasSentToYou?.data[0]?.value,
                    // Not gated by messagesLoaded on purpose: a missing template key for a
                    // language must not hang the page. SendMessage falls back if undefined.
                    messageTemplate: getMessageTemplate?.data?.[0]?.value,
                    messageTemplatePassive: getMessageTemplatePassive?.data?.[0]?.value
                }

                return {
                    status: "success",
                    data: {
                        request: request,
                        requestHistory: getSupportNeed.data,
                        requestComments: getRequestComments.data,
                        codes: getCodes.data[0],
                        comments: getComments.data[0].value,
                        columns: getColumnConfig.data[0],
                        smsEnabled: getSMSSendEnabled.data[0].value,
                        requestSeenSmsEnabled: getRequestSeenSmsEnabled.data[0].value,
                        messages: messagesObj,
                        survey: getSurvey.data[0]
                    },
                    reFetchSupportNeed: getSupportNeed.refetch,
                    reFetchRequestComments: getRequestComments.refetch
                };
            } else {
                return { status: "loading", data: {} };
            }
        }
    }

    // In case none of the above conditions match
    return { status: "loading", data: {} };
}

/*
function GetRequestWithId(requestId) {
    const intl = useIntl()
    const getSupportNeed = GetSupportNeed(requestId)
    const getCodes = GetCodes()
    const getComments = GetCommentOptions()
    const getColumnConfig = GetColumnConfig()
    const getRequestComments = GetRequestComments(requestId)
    const getSMSSendEnabled = GetSMSSendEnabled()
    const getRequestSeenSmsEnabled = GetRequestSeenSms()
    // Get contact information
    const [contactId, setContactId] = useState(null)
    const getContact = GetContact(contactId, contactId !== null)
    // Get survey Information
    const [surveyId, setSurveyId] = useState(null)
    const getSurvey = GetSurveyWithId(surveyId, surveyId !== null)
    const {instanceLang} = useLanguageData()
    // Get UI texts based on survey language
    const [lang, setLang] = useState(null)
    const getProviderHasSeenYourMessageText = GetProviderHasSeenYourMessageText(lang, lang !== null)
    const getMessageSeenText = GetMessageSeenText(lang, lang !== null)
    const getSentYouAMessage = GetSentYouAMessage(lang, lang !== null)
    const getMessageWasSentToYou = GetMessageWasSentToYou(lang, lang !== null)

    const loading = getSupportNeed.isLoading || getCodes.isLoading || getComments.isLoading || getColumnConfig.isLoading || getRequestComments.isLoading || getSMSSendEnabled.isLoading || getRequestSeenSmsEnabled.isLoading
    const error = getSupportNeed.isError || getCodes.isError || getComments.isError || getColumnConfig.isError || getRequestComments.isError || getSMSSendEnabled.isError || getRequestSeenSmsEnabled.isError
    const success = getSupportNeed.isSuccess && getCodes.isSuccess && getComments.isSuccess && getColumnConfig.isSuccess && getRequestComments.isSuccess && getSMSSendEnabled.isSuccess && getRequestSeenSmsEnabled.isSuccess
    const unAuthorized = error && getSupportNeed.error?.response?.status === 401

    useEffect(() => {
        if (success) {
            const request = getSupportNeed?.data.reduce((max, obj) => (obj.id > max.id ? obj : max), { id: -Infinity })
            if (request?.contact !== contactId) {
                setContactId(request?.contact)
            }
            if (request?.survey !== surveyId) {
                setSurveyId(request?.survey)
            }
        }
    }, [getSupportNeed.data, success, contactId, surveyId])

    useEffect( () => {
        if (surveyId && getSurvey.isSuccess) {
            if () {
                setLang(surveyLanguage)
            }
        }
    }, [getSurvey.data, surveyId, getSurvey.isSuccess])

    if (error) {
        if (unAuthorized) {
            return {status: "401", data:{}}
        }
        return {status: "error", data:{}}
    }
    if (loading) {
        return {status: "loading", data:{}}
    }
    if (success) {
        if (getSupportNeed.data.length === 0) {
            return {status: "404", data:{}}
        } else {
            let request = getSupportNeed?.data.reduce((max, obj) => (obj.id > max.id ? obj : max), { id: -Infinity })

            if (getContact.isError || getSurvey.isError) return {status: "error", data:{}}
            if (getContact.isSuccess && contactId && getSurvey.isSuccess && surveyId) {
                const contact = getContact.data[0]
                request.contactmeta = contact?.meta
                request.studentName = (request?.contactmeta.hasOwnProperty("firstname") && request?.contactmeta.hasOwnProperty("lastname")) ?
                    request.contactmeta.firstname + " " + request.contactmeta.lastname
                    : request?.contactmeta ?
                        intl.formatMessage({id:"missingName", defaultMessage:"Missing name"})
                        : intl.formatMessage({id:"dataExpired", defaultMessage:"Student info expired"})

                // set survey language if applicable, otherwise use instance lang
                const survey = getSurvey.data[0]
                const surveyLanguage = survey?.config?.language ? survey.config.language : instanceLang
                request.surveyLanguage = surveyLanguage

                request.categoryName = getCodes.data[0].hasOwnProperty(request.category) ? getCodes.data[0][request.category]["title"] : null

                if (getProviderHasSeenYourMessageText.isError || getMessageSeenText.isError || getSentYouAMessage.isError || getMessageWasSentToYou.isError) return {status: "error", data:{}}
                if (getProviderHasSeenYourMessageText.isLoading || getMessageSeenText.isLoading || getSentYouAMessage.isLoading || getMessageWasSentToYou.isLoading) return {status: "loading", data:{}}

                const messagesObj = {
                    hasSeenYourMessageAndWillContact: getProviderHasSeenYourMessageText.isSuccess ? getProviderHasSeenYourMessageText?.data[0]?.value : "",
                    messageSeenAndWillContact: getMessageSeenText.isSuccess ? getMessageSeenText?.data[0]?.value : "",
                    sentYouAMessage: getSentYouAMessage.isSuccess ? getSentYouAMessage?.data[0]?.value : "",
                    aMessageWasSentToYou : getMessageWasSentToYou.isSuccess ? getMessageWasSentToYou?.data[0]?.value : "",
                }

                return {
                    status: "success",
                    data: {
                        request: request,
                        requestHistory: getSupportNeed?.data,
                        requestComments: getRequestComments?.data,
                        codes: getCodes?.data ? getCodes.data[0] : {},
                        comments: getComments?.data ? getComments.data[0].value : [],
                        columns: getColumnConfig?.data ? getColumnConfig.data[0] : {},
                        smsEnabled : getSMSSendEnabled?.data[0]?.value,
                        requestSeenSmsEnabled: getRequestSeenSmsEnabled?.data[0]?.value,
                        messages : messagesObj
                    },
                    reFetchSupportNeed: () => {
                        getSupportNeed.refetch()
                    },
                    reFetchRequestComments: () => {
                        getRequestComments.refetch()
                    }
                }
            } else {
                return {status: "loading", data:{}}
            }
        }
    }
}
 */
